// src/pages/reviewer/ReviewerSubmissionDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SubmissionSectionTree from "./SubmissionSectionTree";
import { IDSBreadcrumbs, IDSAlert } from "@inera/ids-react";
import { formatDateTimeNoSeconds } from "../../utils/dateTime";
import { getSubmissionStatusLabel } from "../../utils/submissionStatus";
import { getReviewerSubmission } from "../../services/E2EAPI";
import {
  fetchE2EUserProfilesByIds,
  getE2EUserProfileDetails as getUserProfileDetails,
  resolveE2EUserDisplayName,
} from "../../utils/e2eUserProfiles";

export default function ReviewerSubmissionDetails() {
  const { submissionId } = useParams();
  const { isAuthenticated } = useAuth();

  const [submission, setSubmission] = useState(null);
  const [userProfilesById, setUserProfilesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch the submission on mount
  useEffect(() => {
    async function fetchSubmission() {
      try {
        setLoading(true);
        setError("");

        const data = await getReviewerSubmission(submissionId);
        setSubmission(data);

        try {
          const profiles = await fetchE2EUserProfilesByIds([data?.userId]);
          setUserProfilesById(profiles);
        } catch (profileErr) {
          console.error("Failed to load user profile", profileErr);
        }
      } catch (err) {
        console.error("Failed to load submission", err);
        setError("Kunde inte ladda inlämningen.");
      } finally {
        setLoading(false);
      }
    }

    if (submissionId && isAuthenticated) fetchSubmission();
  }, [submissionId, isAuthenticated]);

  if (loading) return <p className="ids-mt-4">Laddar inlämning…</p>;
  if (error) return (
    <div className="ids-m-6"><main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>
      <IDSAlert type="error"><p>{error}</p></IDSAlert>
    </main></div>
  );
  if (!submission || !submission.form)
    return <p className="ids-mt-4">Inlämningen saknar formulärdata.</p>;

  const { status, updatedAt, form, userId } = submission;
  const { formTitle, version, description, sections = [] } = form;
  const submissionUserProfile = getUserProfileDetails(userId, userProfilesById);
  const userDisplayName =
    submissionUserProfile?.name || resolveE2EUserDisplayName(userId, userProfilesById);
  const userEmail = submissionUserProfile?.email || "—";
  const userOrganization = submissionUserProfile?.organizationName || "—";

  return (
    <div className="ids-m-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>

        <div className="ids-mb-6">
          <IDSBreadcrumbs lead="Du är här:">
            <Link to="/start">Start</Link>
            <Link to="/e2e/reviewer/submissions">E2E Granskarportal</Link>
              <a>
                 Visa formulär
              </a>
          </IDSBreadcrumbs>
        </div>

        <h2 className="ids-heading-l ids-mb-6">Visa formulär</h2>

        {/* Submission metadata */}
        <div className="ids-mb-6">
          <p className="ids-mb-2"><strong>Formulär: </strong>{formTitle} v{version}</p>
          {description && <p className="ids-mb-2">{description}</p>}
          <p className="ids-mb-2"><strong>Användare: </strong>{userDisplayName}</p>
          <p className="ids-mb-2"><strong>Mejladress: </strong>{userEmail}</p>
          <p className="ids-mb-2"><strong>Organisation: </strong>{userOrganization}</p>
          <p className="ids-mb-2"><strong>Status: </strong>{getSubmissionStatusLabel(status)}</p>
          <p className="ids-mb-2"><strong>Slutförd: </strong> 
            {updatedAt
              ? formatDateTimeNoSeconds(updatedAt)
              : "—"}
          </p>
        </div>

        <hr className="ids-mb-6" />

        {sections.length === 0 && <p>Inga sektioner hittades.</p>}
        <SubmissionSectionTree sections={sections} />

      </main>
    </div>
  );
}
